import {
  typeConverter,
  useFirebaseAnalytics,
  useFirestore,
  useFirestoreResults,
  useFirestoreSnapshot
} from '@goschool/react-firebase'
import {useCallback, useMemo} from 'react'
import type {
  QueryCompositeFilterConstraint,
  QueryDocumentSnapshot,
  QueryFieldFilterConstraint
} from 'firebase/firestore'
import {
  addDoc,
  and,
  collection,
  doc,
  or,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where
} from 'firebase/firestore'
import type {GoSchoolInvitation} from '../GoSchoolInvitation'
import type {Organization} from '../Organization'
import type {Course} from '../Course'
import {maxBy} from 'lodash'
import {logEvent} from 'firebase/analytics'
import type {User} from 'firebase/auth'
import type {GoSchoolUser} from '../GoSchoolUser'

export function useInvitations(
  organization: QueryDocumentSnapshot<Organization> | null | undefined,
  showExpired = true
) {
  const firestore = useFirestore()


  const invitations = useFirestoreResults(useMemo(
    () => {
      if (organization == null) {
        return null
      }
      const invitationsCollection = collection(firestore, 'invitations').withConverter(typeConverter<GoSchoolInvitation>())
      const constraints: (QueryCompositeFilterConstraint | QueryFieldFilterConstraint)[] = [
        where('organization', '==', organization?.ref)
      ]
      if (!showExpired) {
        constraints.push(or(
          where('expires_at', '==', null),
          where('expires_at', '>', Timestamp.now())
        ))
      }
      return query(
        invitationsCollection,
        and(...constraints),
        orderBy('created_at', 'desc')
      )
    }, [firestore, organization, showExpired]))
  return invitations
}


export function useAccepted(snapshot: QueryDocumentSnapshot<GoSchoolInvitation>) {
  return useFirestoreResults(useMemo(
    () => collection(snapshot.ref, 'acceptance'),
    [snapshot]
  ))
}

export function useIssuedBy(snapshot: QueryDocumentSnapshot<GoSchoolInvitation>) {
  const firestore = useFirestore()

  return useFirestoreSnapshot(useMemo(
    () => {
      const invitation = snapshot.data()
      return doc(firestore, 'users', invitation.created_by)
        .withConverter(typeConverter<GoSchoolUser>())
    },
    [firestore, snapshot]
  ), {watch: false})
}


export function useCourseStudentInvitations(courseSnapshot: QueryDocumentSnapshot<Course>, user: User | null | undefined) {
  const firestore = useFirestore()

  const invitationsCollection = useMemo(
    () => {
      return collection(firestore, 'invitations').withConverter(typeConverter<GoSchoolInvitation>())
    }, [firestore]
  )

  const invitations = useFirestoreResults(useMemo(
    () => {
      if (user == null) {
        return null
      }
      return query(
        invitationsCollection,
        and(
          where('course', '==', courseSnapshot.ref),
          where('roles', '==', ['student']),
          where('email', '==', null)
        )
      )
    },
    [courseSnapshot.ref, invitationsCollection, user]
  ))

  const invitation = useMemo(
    () => {
      const nowTs = Timestamp.now().toMillis()

      if (invitations === undefined) {
        return undefined
      }

      const validInvitations = invitations?.filter(
        (x) => {
          const expiresAt = x.data().expires_at?.toMillis()
          return expiresAt == null || expiresAt > nowTs
        })
      return maxBy(
        validInvitations,
        (s) => s.data().expires_at?.toMillis() ?? Number.MAX_VALUE
      ) ?? null
    },
    [invitations]
  )

  const {analytics} = useFirebaseAnalytics()
  const createInvitation = useCallback(
    async (expirationInHours: number) => {
      if (user == null) {
        throw new Error('User not authenticated')
      }
      if (courseSnapshot.ref.parent.parent == null) {
        throw new Error('Course not in organization')
      }
      if (invitation?.exists) {
        throw new Error('Invitation already exists')
      }
      const expirationInMillis = 1000 * 60 * 60 * expirationInHours
      const expires_at = Timestamp.fromMillis(Timestamp.now().toMillis() + expirationInMillis)
      const organization = courseSnapshot.ref.parent.parent.withConverter(typeConverter<Organization>())
      try {
        const ref = await addDoc(invitationsCollection, {
          created_at: serverTimestamp(), expires_at, created_by: user.uid,
          roles: ['student'], email: null,
          course: courseSnapshot.ref, organization
        })

        logEvent(analytics, 'invitation_created', {
          course_id: courseSnapshot.id,
          invitation_id: ref.id
        })

      } catch (e) {
        console.error('cannot create invitation', e)
      }
    }, [user, courseSnapshot, invitation, invitationsCollection, analytics]
  )


  const extendInvitation = useCallback(
    async (expirationInHours: number) => {
      const currentExpiresAt = invitation?.data().expires_at
      if (invitation == null || currentExpiresAt == null) {
        throw new Error('No invitation to extend')
      }


      const expirationInMillis = 1000 * 60 * 60 * expirationInHours
      const expires_at = Timestamp.fromMillis(currentExpiresAt.toMillis() + expirationInMillis)
      try {
        await updateDoc(invitation.ref, {expires_at})
      } catch (e) {
        console.error('cannot extend invitation', e)
      }
    }, [invitation]
  )

  const invalidateInvitation = useCallback(
    async () => {
      if (invitation == null) {
        throw new Error('No invitation to extend')
      }


      const expires_at = Timestamp.now()
      try {
        await updateDoc(invitation.ref, {expires_at})
      } catch (e) {
        console.error('cannot extend invitation', e)
      }
    }, [invitation]
  )
  return {invitation, createInvitation, extendInvitation, invalidateInvitation}
}


export function useCourseInstructorInvitations(courseSnapshot: QueryDocumentSnapshot<Course>, user: User | null | undefined) {
  const firestore = useFirestore()

  const invitationsCollection = useMemo(
    () => {
      return collection(firestore, 'invitations').withConverter(typeConverter<GoSchoolInvitation>())
    }, [firestore]
  )

  const invitations = useFirestoreResults(useMemo(
    () => {
      if (user == null) {
        return null
      }
      return query(
        invitationsCollection,
        and(
          where('course', '==', courseSnapshot.ref),
          where('roles', '==', ['instructor']),
          where('email', '!=', null),
          where('created_by', '==', user.uid)
        )
      )
    },
    [courseSnapshot, invitationsCollection, user]
  ))

  const {analytics} = useFirebaseAnalytics()
  const createInvitation = useCallback(
    async (expirationInHours: number) => {
      if (user == null) {
        throw new Error('User not authenticated')
      }
      if (courseSnapshot.ref.parent.parent == null) {
        throw new Error('Course not in organization')
      }

      const expirationInMillis = 1000 * 60 * 60 * expirationInHours
      const expires_at = Timestamp.fromMillis(Timestamp.now().toMillis() + expirationInMillis)
      const organization = courseSnapshot.ref.parent.parent.withConverter(typeConverter<Organization>())
      try {
        const ref = await addDoc(invitationsCollection, {
          created_at: serverTimestamp(), expires_at, created_by: user.uid,
          roles: ['student'], email: null,
          course: courseSnapshot.ref, organization
        })

        logEvent(analytics, 'invitation_created', {
          course_id: courseSnapshot.id,
          invitation_id: ref.id
        })

      } catch (e) {
        console.error('cannot create invitation', e)
      }
    }, [user, courseSnapshot, invitationsCollection, analytics]
  )


  return {invitations, createInvitation}
}
